import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { useCabinetId } from '@/hooks/useCabinetMemberMe';
import type { CanvasDraft } from '@hector/shared-types';
import { canvasDraftApi } from '../api/canvasDraft.api';

/** Les deux listes du menu « Ouvrir » — à invalider ensemble après une suppression. */
export function clesDesListesDeBrouillons(cabinetId: string | null | undefined, conversationId: string): unknown[][] {
  return [
    ['canvas-drafts', 'conversation', cabinetId, conversationId],
    ['canvas-drafts', 'recent', cabinetId],
  ];
}

/**
 * Les documents de CETTE conversation — back de « Documents de cette
 * conversation ».
 *
 * Calqué sur `useRecentDrafts` : `enabled` piloté par l'ouverture du dropdown
 * (on ne fetch qu'à la demande, pas au montage du bandeau) et `staleTime: 0`,
 * pour qu'un document nommé ou enregistré juste avant apparaisse à jour dès la
 * réouverture du menu, sans câbler d'invalidation depuis l'auto-save.
 */
export function useConversationDrafts(options: { conversationId: string; enabled: boolean }): {
  drafts: CanvasDraft[];
  isLoading: boolean;
  invalider: () => void;
} {
  const cabinetId = useCabinetId();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['canvas-drafts', 'conversation', cabinetId, options.conversationId],
    queryFn: () => canvasDraftApi.listByConversation(options.conversationId),
    enabled: options.enabled && !!cabinetId,
    staleTime: 0,
  });
  // Les DEUX listes : un brouillon supprimé ici disparaît aussi des « récents »,
  // qui sont transverses au cabinet — ne rafraîchir que celle-ci laisserait le
  // document supprimé cliquable deux lignes plus bas.
  const invalider = React.useCallback(() => {
    for (const queryKey of clesDesListesDeBrouillons(cabinetId, options.conversationId)) {
      void queryClient.invalidateQueries({ queryKey });
    }
  }, [queryClient, cabinetId, options.conversationId]);
  return { drafts: query.data ?? [], isLoading: query.isLoading, invalider };
}
